(() => {
  const initMaterialLab = (root) => {
    if (!root || root.dataset.evemxReady === "true") return;

    const tabs = Array.from(root.querySelectorAll("[data-material-tab]"));
    const panels = Array.from(root.querySelectorAll("[data-material-panel]"));
    const copies = Array.from(root.querySelectorAll("[data-material-copy]"));

    if (!tabs.length || !panels.length) return;
    root.dataset.evemxReady = "true";

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let activeIndex = Math.max(0, tabs.findIndex((tab) => tab.classList.contains("is-active")));
    let isNearViewport = false;

    const prepareVideo = (video, loadSource) => {
      if (!video) return;

      if (!video.getAttribute("poster") && video.dataset.poster) {
        video.setAttribute("poster", video.dataset.poster);
      }

      if (!loadSource || reducedMotion.matches || video.getAttribute("src") || !video.dataset.src) return;

      video.setAttribute("src", video.dataset.src);
      video.load();
    };

    const pauseAll = () => {
      panels.forEach((panel) => panel.querySelector("video")?.pause());
    };

    const playActive = () => {
      const video = panels[activeIndex]?.querySelector("video");
      if (!video || !isNearViewport) return;

      prepareVideo(video, true);
      if (reducedMotion.matches || !video.getAttribute("src")) return;

      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === "function") {
        playAttempt.catch(() => {});
      }
    };

    const activate = (index, focusTab = false) => {
      activeIndex = index;

      tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === activeIndex;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", active ? "true" : "false");
        tab.setAttribute("tabindex", active ? "0" : "-1");

        if (active && focusTab) tab.focus();
      });

      panels.forEach((panel, panelIndex) => {
        const active = panelIndex === activeIndex;
        const video = panel.querySelector("video");

        panel.classList.toggle("is-active", active);
        panel.hidden = !active;

        if (!active) video?.pause();
      });

      copies.forEach((copy, copyIndex) => {
        copy.hidden = copyIndex !== activeIndex;
      });

      if (isNearViewport) playActive();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener("click", () => activate(index));
      tab.addEventListener("keydown", (event) => {
        if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
        event.preventDefault();

        let nextIndex = index;
        if (event.key === "ArrowLeft") nextIndex = (index - 1 + tabs.length) % tabs.length;
        if (event.key === "ArrowRight") nextIndex = (index + 1) % tabs.length;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = tabs.length - 1;

        activate(nextIndex, true);
      });
    });

    const handleMotionChange = () => {
      if (reducedMotion.matches) {
        pauseAll();
      } else {
        playActive();
      }
    };

    if (typeof reducedMotion.addEventListener === "function") {
      reducedMotion.addEventListener("change", handleMotionChange);
    } else if (typeof reducedMotion.addListener === "function") {
      reducedMotion.addListener(handleMotionChange);
    }

    let observer;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          isNearViewport = entry.isIntersecting;
          if (isNearViewport) {
            playActive();
          } else {
            pauseAll();
          }
        },
        { rootMargin: "300px 0px", threshold: 0.01 }
      );
      observer.observe(root);
    } else {
      isNearViewport = true;
    }

    activate(activeIndex);
    if (isNearViewport) playActive();

    root.evemxCleanup = () => {
      observer?.disconnect();
      pauseAll();
      if (typeof reducedMotion.removeEventListener === "function") {
        reducedMotion.removeEventListener("change", handleMotionChange);
      } else if (typeof reducedMotion.removeListener === "function") {
        reducedMotion.removeListener(handleMotionChange);
      }
      delete root.evemxCleanup;
      delete root.dataset.evemxReady;
    };
  };

  const initSignatureSlider = (root) => {
    if (!root || root.dataset.signatureReady === "true") return;

    const rail = root.querySelector("[data-signature-rail]");
    const cards = Array.from(rail?.querySelectorAll(".evx-product-card") || []);
    const dots = Array.from(root.querySelectorAll("[data-signature-dot]"));
    if (!rail || !cards.length || !dots.length) return;

    root.dataset.signatureReady = "true";
    let frame = 0;

    const activeIndex = () => {
      const railLeft = rail.getBoundingClientRect().left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - railLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });

      return closestIndex;
    };

    const updateDots = () => {
      frame = 0;
      const index = activeIndex();
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === index;
        dot.classList.toggle("is-active", active);
        dot.setAttribute("aria-current", active ? "true" : "false");
      });
    };

    const queueUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(updateDots);
    };

    dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        cards[index]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
      });
    });

    rail.addEventListener("scroll", queueUpdate, { passive: true });
    window.addEventListener("resize", queueUpdate, { passive: true });
    updateDots();

    root.evemxSignatureCleanup = () => {
      rail.removeEventListener("scroll", queueUpdate);
      window.removeEventListener("resize", queueUpdate);
      if (frame) window.cancelAnimationFrame(frame);
      delete root.evemxSignatureCleanup;
      delete root.dataset.signatureReady;
    };
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll("[data-evemx-materials]").forEach(initMaterialLab);
    scope.querySelectorAll("[data-signature-slider]").forEach(initSignatureSlider);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelectorAll("[data-evemx-materials]").forEach((root) => root.evemxCleanup?.());
    event.target.querySelectorAll("[data-signature-slider]").forEach((root) => root.evemxSignatureCleanup?.());
  });
})();
